<script lang="ts" setup>
// Imports
import { ref, provide, watch, onMounted } from 'vue'
import Drawer from 'primevue/drawer'
import DynamicDialog from 'primevue/dynamicdialog'
import PanelMenu from 'primevue/panelmenu'
import { useAdminStore } from '~/stores/shared/admin'
import { useBusinessStore } from '~/stores/shared/business'

// Composables
const { t } = useI18n()

useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })
const businessStore = useBusinessStore()
const { businessId: buuid, businessInfo } = storeToRefs(businessStore)

// Payment warning state
const paymentWarning = ref<{ enabled: boolean, days_remaining: number } | null>(null)

// Fetch business data including subscription status
async function fetchBusinessData() {
  try {
    const data = await $fetch('/api/user/business')
    if (data?.payment_warning) {
      paymentWarning.value = data.payment_warning
    }
  }
  catch (error) {
    // Error is handled by the API (redirect to /paused if needed)
    console.error('Error fetching business data:', error)
  }
}

// Fetch on mount
onMounted(() => {
  fetchBusinessData()
})

// Initialize expanded menu state
onMounted(() => {
  updateExpandedKeys()
})
// Refs
const user = ref()
const menu = ref(false)
const route = useRoute()

// Add active class to menu items dynamically
const addActiveClass = (items: any[], currentPath: string): any[] => {
  return items.map((item) => {
    const newItem = { ...item }

    // Add active class if URL matches current path
    if (newItem.url && newItem.url === currentPath) {
      newItem.class = 'router-link-active'
    }

    // Recursively process nested items
    if (newItem.items) {
      newItem.items = addActiveClass(newItem.items, currentPath)
    }

    return newItem
  })
}

const baseMenuItems = computed(() => [
  {
    key: '0',
    label: t('Point of sale'),
    icon: 'la:store',
    url: '/store/pos',
  },
  {
    key: '1',
    label: t('POS & Orders'),
    icon: 'pi pi-shopping-bag',
    items: [
      ...(businessInfo.value?.service_type === 'counter'
        ? [
            {
              label: t('Orders'),
              icon: 'streamline:money-cashier-shop-shopping-pay-payment-cashier-store-cash-register-machine',
              url: '/app/orders',
            },
            {
              label: t('Deleted Orders'),
              icon: 'carbon:trash-can',
              url: '/app/orders/audit',
            },
          ]
        : []),
      ...(businessInfo.value?.service_type === 'restaurant'
        ? [
            {
              label: t('Checks'),
              icon: 'carbon:receipt',
              url: '/app/checks',
            },
            {
              label: t('Deleted Checks'),
              icon: 'carbon:trash-can',
              url: '/app/checks/audit',
            },
          ]
        : []),
    ],
  },
  {
    key: '2',
    label: t('Financial'),
    icon: 'pi pi-dollar',
    items: [
      {
        label: t('Reports'),
        icon: 'carbon:analytics',
        url: '/app/reports',
      },
      {
        label: t('Shifts'),
        icon: 'carbon:cloud-service-management',
        url: '/app/shifts',
      },
      {
        label: t('Expenses'),
        icon: 'carbon:money',
        url: '/app/expenses',
      },
      {
        label: t('Expense Types'),
        icon: 'carbon:tag',
        url: '/app/expenses/types',
      },
      {
        label: t('Fund Sources'),
        icon: 'carbon:wallet',
        url: '/app/expenses/sources',
      },
      {
        label: t('Financial Overview'),
        icon: 'carbon:chart-pie',
        url: '/app/financial',
      },
      {
        label: t('Discounts'),
        icon: 'circum:discount-1',
        url: '/app/discounts',
      },
    ],
  },
  {
    key: '3',
    label: t('Inventory'),
    icon: 'pi pi-box',
    items: [
      {
        label: t('Products'),
        icon: 'carbon:shopping-catalog',
        url: '/app/products',
      },
      {
        label: t('Import Products'),
        icon: 'carbon:document-import',
        url: '/app/products/import',
      },
      ...(businessInfo.value?.pos_config?.enable_combos === true
        ? [{
            label: t('Combos'),
            icon: 'carbon:shopping-catalog',
            url: '/app/combos',
          }]
        : []),
      {
        label: t('Categories'),
        icon: 'carbon:collapse-categories',
        url: '/app/categories',
      },
      {
        label: t('Modifiers'),
        icon: 'carbon:settings-adjust',
        url: '/app/modifiers',
      },
      {
        label: t('Recipes'),
        icon: 'carbon:document',
        url: '/app/recipes',
      },
      {
        label: t('Ingredients'),
        icon: 'carbon:wheat',
        url: '/app/ingredients',
      },
      {
        label: t('Stock Items'),
        icon: 'carbon:package',
        url: '/app/stock-items',
      },
      {
        label: t('Inventory Reports'),
        icon: 'carbon:inventory-management',
        url: '/app/inventory/reports',
      },
      {
        label: t('Stock Levels'),
        icon: 'carbon:chart-bar',
        url: '/app/inventory/reports/stock-levels',
      },
    ],
  },
  ...(businessInfo.value?.service_type === 'restaurant'
    ? [{
        key: '4',
        label: t('Restaurant'),
        icon: 'pi pi-building',
        items: [
          {
            label: t('Dining Areas'),
            icon: 'carbon:table-split',
            url: '/app/dining-areas',
          },
          {
            label: t('Tables'),
            icon: 'carbon:table',
            url: '/app/tables',
          },
          {
            label: t('Servers'),
            icon: 'carbon:user-multiple',
            url: '/app/servers',
          },
          ...(businessInfo.value?.pos_config?.enable_kds !== false
            ? [{
                label: t('KDS Stations'),
                icon: 'carbon:screen',
                url: '/app/kds-stations',
              }]
            : []),
        ],
      }]
    : []),
  {
    key: '5',
    label: t('Business'),
    icon: 'pi pi-briefcase',
    items: [
      {
        label: t('Business'),
        icon: 'clarity:details-line',
        url: '/app/business',
      },
      {
        label: t('Users'),
        icon: 'carbon:user',
        url: '/app/users',
      },
      {
        label: t('Payment methods'),
        icon: 'ph:credit-card',
        url: '/app/payment/settings',
      },
      {
        label: t('Customers'),
        icon: 'carbon:user',
        url: '/app/customers',
      },
    ],
  },
  {
    key: '6',
    label: t('System'),
    icon: 'pi pi-cog',
    items: [
      {
        label: t('System Configuration'),
        icon: 'carbon:settings-adjust',
        url: '/app/system/pos-config',
      },
      {
        label: t('Cache'),
        icon: 'carbon:storage-pool',
        url: '/app/cache',
      },
    ],
  },
])

// Create computed menu items with active class
const menuItems = computed(() => addActiveClass(baseMenuItems.value, route.path))

// Track which groups should be expanded based on current route
const expandedKeys = ref<Record<string, boolean>>({})

// Initialize and update expanded state based on route
const updateExpandedKeys = () => {
  const path = route.path
  const newExpandedKeys: Record<string, boolean> = {}

  baseMenuItems.value.forEach((item) => {
    if (item.items) {
      const hasActiveChild = item.items.some((child: any) => child.url === path)
      if (hasActiveChild && item.key) {
        newExpandedKeys[item.key] = true
      }
    }
  })

  expandedKeys.value = newExpandedKeys
}

// Watch route changes to update expanded state
watch(() => route.path, () => {
  updateExpandedKeys()
})

// Stores
const adminStore = useAdminStore()
const { businessName } = storeToRefs(adminStore)

// Methods
function logOut() {
  navigateTo('/auth/logout')
}

function switchBusiness() {
  navigateTo('/auth/switch-business')
}

const toggle = () => {
  menu.value = !menu.value
}

// Side effects
// watch(buuid, (newBuuid: string | null | undefined) => {
//   user.value = useSupabaseUser().value
//   if (newBuuid) {
//     navigation.value.unshift({
//       href: `/store/${newBuuid}`,
//       name: 'Point of sale',
//       icon: 'la:store',
//     })
//   }
// }, { immediate: true })

onMounted(async () => {
  if (buuid.value) {
    await adminStore.getBusinessInfo(buuid.value)
  }
})

provide('buuid', buuid)

useSupabaseClient().auth.onAuthStateChange((event) => {
  const accessTokenCookie = useCookie('sb-access-token')
  const refreshTokenCookie = useCookie('sb-refresh-token')
  if (event === 'SIGNED_OUT') {
    accessTokenCookie.value = null
    refreshTokenCookie.value = null
    localStorage.setItem('tokenRefreshDate', '0')
  }
})
</script>

<template>
  <div>
    <!-- UI Components -->
    <DynamicDialog />
    <Toast />

    <!-- Mobile Drawer -->
    <Drawer
      v-model:visible="menu"
      header=""
      :pt="{
        header: { class: 'py-2 px-3' },
        content: { class: 'p-0' },
      }"
    >
      <div class="flex h-full flex-col">
        <div class="h-15 flex items-center justify-center" />
        <div class="flex-1 overflow-auto">
          <PanelMenu
            v-model:expanded-keys="expandedKeys"
            :model="menuItems"
            class="w-full"
          >
            <template #itemicon="{ item }">
              <span
                v-if="item.icon && item.icon.startsWith('pi ')"
                :class="item.icon"
              />
              <Icon
                v-else-if="item.icon"
                :name="item.icon"
              />
            </template>
            <template #headericon="{ item }">
              <span
                v-if="item.icon && item.icon.startsWith('pi ')"
                :class="item.icon"
              />
              <Icon
                v-else-if="item.icon"
                :name="item.icon"
              />
            </template>
          </PanelMenu>
        </div>
        <div
          v-if="user && user?.email"
          class="mt-auto border-t p-3"
        >
          <span class="font-medium text-sm">{{ user.email }}</span>
        </div>
      </div>
    </Drawer>

    <!-- Main Layout -->
    <div class="page-wrapper flex flex-col lg:flex-row">
      <!-- Desktop Sidebar -->
      <div
        id="sidebar"
        class="hidden min-w-[250px] shadow-xl lg:flex lg:flex-col print:hidden"
      >
        <div class="flex-1 overflow-auto p-2">
          <PanelMenu
            v-model:expanded-keys="expandedKeys"
            :model="menuItems"
            class="w-full"
          >
            <template #itemicon="{ item }">
              <span
                v-if="item.icon && item.icon.startsWith('pi ')"
                :class="item.icon"
              />
              <Icon
                v-else-if="item.icon"
                :name="item.icon"
              />
            </template>
            <template #headericon="{ item }">
              <span
                v-if="item.icon && item.icon.startsWith('pi ')"
                :class="item.icon"
              />
              <Icon
                v-else-if="item.icon"
                :name="item.icon"
              />
            </template>
          </PanelMenu>
        </div>
        <div
          v-if="user && user?.email"
          class="mt-auto border-t p-3"
        >
          <span class="font-medium text-sm">{{ user.email }}</span>
        </div>
      </div>

      <!-- Content Area -->
      <div
        id="content"
        class="min-h-screen grow overflow-x-scroll print:overflow-x-hidden"
      >
        <!-- Top Bar -->
        <div class="top-bar flex py-2 shadow-md shadow-surface-200 print:hidden">
          <div class="flex grow items-center">
            <a
              class="ml-2 block cursor-pointer lg:hidden"
              @click.prevent="toggle"
            >
              <i class="pi pi-bars" />
            </a>
            <h2
              v-if="businessName"
              class="text-base my-0 pl-2"
            >
              {{ businessName }}
            </h2>
          </div>
          <div class="flex items-center gap-2 pr-2">
            <Button
              v-if="businessStore.hasMultipleBusinesses"
              type="button"
              :label="t('Switch business')"
              size="small"
              text
              icon="pi pi-arrow-right-arrow-left"
              @click="switchBusiness"
            />
            <Button
              type="button"
              :label="t('Sign out')"
              size="small"
              outlined
              @click="logOut"
            />
          </div>
        </div>

        <!-- Payment Warning Banner -->
        <PaymentWarningBanner
          v-if="paymentWarning?.enabled"
          :days-remaining="paymentWarning.days_remaining"
        />

        <!-- Main Content -->
        <div class="main-content">
          <div>
            <div class="print:p-0 p-2 md:p-6">
              <div>
                <slot />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
